import Vue from 'vue'

/** La configuration OCPP gardée d'une borne : sa dernière lecture et son historique. */
class ChargingPointConfigurationRepository {

  // { readAt, readRequestedAt, firmwareVersion, keys: [{ key, value, readonly, sensitive, since, lastChange }], changes: [...] }
  // key : l'historique de cette clé seulement (en GET, les paramètres passent par data)
  ofChargingPoint(chargingPointId, key = null) {
    return this.fetch({ url: `/api/charging-points/${chargingPointId}/configuration`, method: 'get', data: key ? { key } : {} })
  }

  // La borne doit être connectée ; sa réponse met la photo à jour
  read(chargingPointId) {
    return this.fetch({ url: `/api/charging-points/${chargingPointId}/configuration/read`, method: 'post' })
  }

  // Le parc (ZEborne seul) ; filters : { manufacturerId, productLineId, chargingPointTypeId, organizationId, locationId }
  // sans key → { chargingPoints, read, keys: [{ key, chargingPoints }] }
  // key → { chargingPoints, read, withKey, sensitive, values: [{ value, chargingPoints }] }
  search(filters, key = null) {
    return this.fetch({ url: '/api/charging-points/configuration/search', method: 'get', data: this.clean({ ...filters, key }) })
  }

  // Les bornes à cette valeur (null : sans valeur ou masquée), ou sans la clé → { total, rows }
  // `values` : à l'une de ces valeurs (les écritures d'un même booléen)
  chargingPoints(filters, key, { value = null, values = null, missing = false, offset = 0, limit = 50 } = {}) {
    return this.fetch({ url: '/api/charging-points/configuration/search', method: 'get', data: this.clean({ ...filters, key, list: 1, value: values ? null : value, values, nullValue: !missing && !values && value === null ? 1 : null, missing: missing ? 1 : null, offset, limit }) })
  }

  // Les clés suivies → { assessed, deviating, targets: [{ layer, id, name }], keys: [{ code, standard, label, description, expected, variants, present, deviating }] }
  trackedKeys(filters = {}) {
    return this.fetch({ url: '/api/charging-points/configuration/tracked-keys', method: 'get', data: this.clean(filters) })
  }

  // Créer une clé (commune, ou propre au constructeur `manufacturerId`), ou régler une clé suivie : { code, manufacturerId, label, description, expected }
  saveTrackedKey(key) {
    return this.fetch({ url: '/api/charging-points/configuration/tracked-keys', method: 'put', data: key })
  }

  // Une clé à nous n'est plus suivie ; une clé standard revient à son réglage d'office
  deleteTrackedKey(code, manufacturerId = null) {
    return this.fetch({ url: `/api/charging-points/configuration/tracked-keys/${encodeURIComponent(code)}${this.ownedBy(manufacturerId)}`, method: 'delete' })
  }

  // La répartition d'une clé suivie → { key, chargingPoints, assessed, present, missing, deviating, values: [{ value, chargingPoints, deviating, readonly }] }
  trackedKey(code, filters = {}) {
    return this.fetch({ url: `/api/charging-points/configuration/tracked-keys/${encodeURIComponent(code)}`, method: 'get', data: this.clean(filters) })
  }

  // Ses bornes à une valeur (null : sans valeur), sans la clé (missing) ou en écart (deviating) → { total, rows }
  // `values` : à l'une de ces valeurs (les écritures d'un même booléen)
  trackedKeyChargingPoints(code, filters, { value = null, values = null, missing = false, deviating = false, offset = 0, limit = 50 } = {}) {
    const which = missing ? { missing: 1 } : (deviating ? { deviating: 1 } : (values ? { values } : (value === null ? { nullValue: 1 } : { value })))
    return this.fetch({ url: `/api/charging-points/configuration/tracked-keys/${encodeURIComponent(code)}`, method: 'get', data: this.clean({ ...filters, ...which, list: 1, offset, limit }) })
  }

  // La clé chez un constructeur, une gamme ou un type : { layer, targetId, versionPattern, key, expected, note } → { id }
  setVariant(code, variant) {
    return this.fetch({ url: `/api/charging-points/configuration/tracked-keys/${encodeURIComponent(code)}/variants`, method: 'put', data: variant })
  }

  removeVariant(code, variantId, manufacturerId = null) {
    return this.fetch({ url: `/api/charging-points/configuration/tracked-keys/${encodeURIComponent(code)}/variants/${variantId}${this.ownedBy(manufacturerId)}`, method: 'delete' })
  }

  // La clé propre à un constructeur se désigne avec lui : la même chaîne ailleurs est une autre clé.
  ownedBy(manufacturerId) {
    return manufacturerId ? `?manufacturerId=${encodeURIComponent(manufacturerId)}` : ''
  }

  // Les modifications en masse, la plus récente d'abord → { total, rows }
  batches({ offset = 0, limit = 50 } = {}) {
    return this.fetch({ url: '/api/charging-points/configuration/batches', method: 'get', data: { offset, limit } })
  }

  // Un lot et ses bornes page par page (status : dans cet état) → { ...lot, itemsTotal, items }
  batch(batchId, { status = null, offset = 0, limit = 50 } = {}) {
    return this.fetch({ url: `/api/charging-points/configuration/batches/${batchId}`, method: 'get', data: this.clean({ status, offset, limit }) })
  }

  // { code, value, allowEmpty, fromValues (null : toutes les autres valeurs), ...filtres, dryRun } → { id, chargingPoints }
  createBatch(batch) {
    return this.fetch({ url: '/api/charging-points/configuration/batches', method: 'post', data: batch })
  }

  // Un nouveau lot qui rend à chaque borne qui l'a acceptée sa valeur d'avant → { id, chargingPoints }
  revertBatch(batchId) {
    return this.fetch({ url: `/api/charging-points/configuration/batches/${batchId}/revert`, method: 'post' })
  }

  // Les changements constatés : { origin, byName, days, offset, limit, ...filtres } → { total, rows }
  changes(filters = {}, { origin = null, byName = null, days = 30, offset = 0, limit = 50 } = {}) {
    return this.fetch({ url: '/api/charging-points/configuration/changes', method: 'get', data: this.clean({ ...filters, origin, byName, days, offset, limit }) })
  }

  cancelBatch(batchId) {
    return this.fetch({ url: `/api/charging-points/configuration/batches/${batchId}/cancel`, method: 'post' })
  }

  clean(params) {
    return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== null && value !== undefined && value !== ''))
  }

  fetch(options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch(options)
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }
}

export default new ChargingPointConfigurationRepository()
